import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronUp } from "react-icons/fa";

import MainContainer from "../../components/MainContainer";
import PaymentModal from "../../components/PaymentModal/PaymentModal";
import {
  Container,
  BackButton,
  Title,
  CurrentPlanSection,
  PlanInfo,
  PlanValue,
  CreditsInfo,
  CreditsLabel,
  CreditsValue,
  BalanceSection,
  SectionTitle,
  SectionHeader,
  Table,
  TableRow,
  TableHeader,
  TableCell,
  TableFooter,
  FooterText,
  Pagination,
  PaginationButton,
  PaginationCurrent,
  PricingSection,
  BillingToggle,
  PlansGrid,
  PlanCard,
  PopularBadge,
  PlanName,
  PlanDescription,
  PriceSection,
  OriginalPrice,
  CurrentPrice,
  PricePeriod,
  YearlyTotal,
  GetStartedButton,
  FeaturesList,
  Feature,
  FAQSection,
  FAQList,
  FAQItem,
  FAQQuestion,
  FAQAnswer,
  LoadingIndicator,
  TableContainer,
  FooterActions,
  Tooltip,
  TimeAgoCell,
  TableLoading,
  HeaderContainer,
  BillingText,
  ToggleContainer,
  ToggleButton,
  SeparatorLine,
  RenewButton,
  CreditsContainer,
} from "./SubscriptionPage.styles";
import ManageSubscriptionModal from "../../components/ManageSubscriptionModal/ManageSubscriptionModal";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCreditsHistory,
  selectFetchSubscriptionsLoading,
  selectSubscriptions,
} from "../../store/slices/subscription/slice";
import {
  choosePlan,
  fetchSubscriptions,
  getSubscriptionsCreditsHistory,
  rechargeCredits,
} from "../../store/slices/subscription/asyncThunks";
import { selectUser } from "../../store/slices/user/slice";
import { faqData } from "../../data/faq";
import RechargeModal from "../../components/RechargeModal/RechargeModal";
import {
  daysDifference,
  formatDate,
  formatTimeAgo,
  priceFormatter,
} from "../../utils/utils";
import PaymentService from "../../api/services/payment";
import { fetchUserInfo } from "../../store/slices/user/asyncThunk";
import { ReactComponent as ChevronSvg } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as CheckSvg } from "../../assets/icons/check.svg";
import { ReactComponent as InfoSvg } from "../../assets/icons/info-circle.svg";
import giftgif from "../../assets/images/gift.gif";

import { useMediaQuery } from "react-responsive";
import GradientStarIcon from "../../components/GradientStarIcon/GradientStarIcon";
import SubscriptionExpiredModal from "../../components/SubscriptionExpiredModal/SubscriptionExpiredModal";

const planConfigurations = {
  Basic: {
    description: "Hobbyist & Creators",
    gradient: "linear-gradient(0deg, #657890 0%,rgb(66, 80, 97) 100%);",

    firstColor: "#FFCDA5",
    features: [
      "300 credits monthly",
      "Unlock all templates (updated daily)",
      "3 videos high speed generation queue",
      "AI training tools",
      "Generate with priority over free users",
      "Top-up rate: 300 credits per $10",
    ],
  },
  Pro: {
    description: "Professionals",
    gradient:
      "radial-gradient(44.08% 42.37% at 50% 0%, rgba(0, 113, 255, 0.50) 0%, rgba(7, 23, 44, 0) 100%), linear-gradient(0deg,rgb(18, 111, 224) 0%, #123A6E 100%);",
    popular: true,
    firstColor: "#FFCDA5",
    features: [
      "1,500 credits monthly",
      "Unlock all templates (updated daily)",
      "10 videos high speed generation queue",
      "AI training tools",
      "Generate with priority over Basic Plan users",
      "Top-up rate: 500 credits per $10",
    ],
  },
  Premium: {
    description: "Studios & Startups",
    gradient:
      "radial-gradient(39.08% 122.37% at 50.78% 0%,rgba(0, 46, 110, 0.36) 0%, rgba(0, 31, 58, 0.00) 100%), #001F3A;",

    firstColor: "#FFCDA5",
    features: [
      "3,000 credits monthly",
      "Unlock all templates (updated daily)",
      "15 videos high speed generation queue",
      "AI training tools",
      "Generate with priority over Pro Plan users",
      "Top-up rate: 600 credits per $10",
    ],
  },
};

const rechargeConfig = {
  name: "Recharge",
  description: "Top up credits",
  price: 10,
  gradient:
    "radial-gradient(39.08% 122.37% at 10.78% 0%, #4CCAA3 0%, rgba(29, 121, 85, 0.00) 100%), linear-gradient(0deg, #2E866D 0%, #1C7753 100%);",

  features: [
    "All the features in your current subscription plan",
    "Credits expire after 30 days",
    "Top up at your current plan rate",
  ],
};

const NOT_EXPIRATION_SALE_LOGS_TYPES = [
  "MODEL_USAGE",
  "AFFILIATE_SUBSCRIPTION",
];

const SubscriptionPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState("yearly");
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [exitingFAQ, setExitingFAQ] = useState(null);
  const [isBalanceExpanded, setIsBalanceExpanded] = useState(false);
  const loggedIn = useSelector(selectLoggedIn);
  const subscriptions = useSelector(selectSubscriptions);
  const loading = useSelector(selectFetchSubscriptionsLoading);
  const user = useSelector(selectUser);
  const {
    loading: creditsHistoryLoading,
    data: creditsHistoryData,
    page: creditsHistoryPage,
    perPage: creditsHistoryPerPage,
    total: creditsHistoryTotal,
  } = useSelector(selectCreditsHistory);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isRechargeModalOpen, setIsRechargeModalOpen] = useState(false);
  const [isSubscriptionExpiredModalOpen, setIsSubscriptionExpiredModalOpen] =
    useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [hoveredStartTime, setHoveredStartTime] = useState(null);
  const [hoveredExpiration, setHoveredExpiration] = useState(null);

  useEffect(() => {
    dispatch(fetchSubscriptions())
      .unwrap()
      .then(() => {})
      .catch((err) => console.log(err.message));
  }, [dispatch]);

  useEffect(() => {
    if (loggedIn) {
      dispatch(getSubscriptionsCreditsHistory(1))
        .unwrap()
        .then(() => {})
        .catch((err) => console.log(err.message));
    }
  }, [loggedIn, dispatch]);

  const handleBack = () => {
    navigate("/");
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });
    }, 300);
  };

  const toggleFAQ = (index) => {
    if (expandedFAQ === index) {
      setExitingFAQ(index);
      setTimeout(() => {
        setExpandedFAQ(null);
        setExitingFAQ(null);
      }, 300);
    } else {
      if (expandedFAQ !== null) {
        setExitingFAQ(expandedFAQ);
        setTimeout(() => {
          setExpandedFAQ(index);
          setExitingFAQ(null);
        }, 300);
      } else {
        setExpandedFAQ(index);
      }
    }
  };

  const toggleBalance = () => {
    setIsBalanceExpanded(!isBalanceExpanded);
  };

  const renewPlan = () => {
    handleSubscriptionExpiredModalClose();
    const activePlan = plans?.find(
      (plan) => plan.name === user?.subscription?.name
    );
    if (user?.subscription?.period) {
      setBillingPeriod(user?.subscription?.period.toLowerCase());
    }
    handleGetStarted(activePlan);
  };

  const handleGetStarted = (plan) => {
    if (loggedIn) {
      setSelectedPlan(plan);
      if (plan.name !== "Recharge") {
        setIsPaymentModalOpen(true);
      } else {
        if (isActiveSubscription) {
          setIsRechargeModalOpen(true);
        } else {
          handleSubscriptionExpiredModalOpen();
        }
      }
    } else {
      setSelectedPlan(plan);
      setIsPaymentModalOpen(true);
      // setIsRechargeModalOpen(true);

      // dispatch(setSignUpModalOpen(true));
    }
  };

  const isMobile = useMediaQuery({ maxWidth: 768 });

  const handleCardPayment = (paymentMethod, price) => {
    if (isPaymentModalOpen) {
      const planId =
        billingPeriod === "yearly"
          ? selectedPlan.yearlyId
          : selectedPlan.monthlyId;

      dispatch(
        choosePlan({
          planId: planId,
          planPeriod: billingPeriod.toUpperCase(),
          paymentMethod,
        })
      )
        .unwrap()
        .then((res) => {
          const link = res.data.link;
          //   const isFirstPurchase = !user.subscription;

          if (isMobile) {
            window.location.href = link;
          } else {
            window.open(link, "_blank");
          }

          checkNewPayment();
        })
        .catch((err) => {
          console.error("Error in choosePlan:", err.message);
          const fallbackLink = "https://t.me/playboxbot";
          const newWindow = window.open(fallbackLink, "_blank");
          if (
            !newWindow ||
            newWindow.closed ||
            typeof newWindow.closed == "undefined"
          ) {
            window.location.href = fallbackLink;
          }
        });
    } else if (isRechargeModalOpen) {
      dispatch(rechargeCredits({ paymentMethod, amount: price }))
        .unwrap()
        .then((res) => {
          const link = res.data.link;
          console.log("Opening recharge link:", link);

          if (isMobile) {
            window.location.href = link;
          } else {
            window.open(link, "_blank");
          }

          checkNewPayment();
        })
        .catch((err) => {
          console.error("Error in rechargeCredits:", err.message);
          const fallbackLink = "https://t.me/PremiumBot";
          const newWindow = window.open(fallbackLink, "_blank");
          if (
            !newWindow ||
            newWindow.closed ||
            typeof newWindow.closed == "undefined"
          ) {
            window.location.href = fallbackLink;
          }
        });
    }
  };

  const modalStateRef = useRef({ isPaymentModalOpen, isRechargeModalOpen });
  useEffect(() => {
    modalStateRef.current = { isPaymentModalOpen, isRechargeModalOpen };
  }, [isPaymentModalOpen, isRechargeModalOpen]);

  const checkNewPayment = () => {
    PaymentService.checkNewPayment()
      .then((result) => {
        const { newPayment } = result;

        if (newPayment) {
          handleClosePaymentModal();
          handleCloseRechargeModal();
          dispatch(fetchUserInfo());
          window.scrollTo(0, 0);
        } else {
          const { isPaymentModalOpen, isRechargeModalOpen } =
            modalStateRef.current;
          if (isPaymentModalOpen || isRechargeModalOpen) {
            setTimeout(checkNewPayment, 5000);
          }
        }
      })
      .catch((err) => {
        console.log(err);
        const { isPaymentModalOpen, isRechargeModalOpen } =
          modalStateRef.current;
        if (isPaymentModalOpen || isRechargeModalOpen) {
          setTimeout(checkNewPayment, 5000);
        }
      });
  };

  const handleClosePaymentModal = () => {
    setIsPaymentModalOpen(false);
    setSelectedPlan(null);
  };

  const handleCloseRechargeModal = () => {
    setIsRechargeModalOpen(false);
    setSelectedPlan(null);
  };

  const handleCloseManageModal = () => {
    setIsManageModalOpen(false);
  };

  const getPlanFeatures = (plan) => {
    const baseFeatures = [
      `${priceFormatter(plan.creditsMonthly)} credits per month`,
      "Access all templates (new ones added daily)",
    ];

    const queueSizes = { Basic: 3, Pro: 10, Premium: 15 };

    return [
      ...baseFeatures,
      `Fast video generation: up to ${queueSizes[plan.name] || 5} at once`,
      "Training tools included",
      "Priority over free users",
      `Top-ups: ${priceFormatter(plan.topUpCredits)} credits for $10`,
    ];
  };

  const userHadSubscription = user?.subscription && user?.subscription.name;

  const isExpiredSubscription =
    user?.subscription?.name &&
    user?.subscription?.endDate &&
    new Date(user?.subscription?.endDate) < new Date();

  const yearlySubscription =
    user?.subscription && user?.subscription?.period === "YEARLY";

  const isActiveSubscription =
    user?.subscription &&
    user?.subscription?.name &&
    user?.subscription?.endDate &&
    new Date(user?.subscription?.endDate) > new Date();

  const getActiveUserCredits = () => {
    if (!user?.subscription?.name) {
      return "300";
    }

    // Find the user's active plan from subscriptions
    const activePlan = subscriptions?.find(
      (sub) =>
        sub.name === user?.subscription?.name &&
        sub.period === user?.subscription?.period
    );

    return activePlan ? `${activePlan.topUpCredits}` : "300";
  };

  const prepareSubscriptionPlans = () => {
    if (!subscriptions || subscriptions.length === 0) return [];

    // Group subscriptions by name
    const groupedPlans = subscriptions.reduce((acc, subscription) => {
      if (!acc[subscription.name]) {
        acc[subscription.name] = {};
      }

      if (subscription.period === "MONTHLY") {
        acc[subscription.name].monthly = subscription;
      } else if (subscription.period === "YEARLY") {
        acc[subscription.name].yearly = subscription;
      }

      return acc;
    }, {});

    const plans = Object.keys(groupedPlans).map((planName) => {
      const config = planConfigurations[planName];
      const monthlyPlan = groupedPlans[planName].monthly;
      const yearlyPlan = groupedPlans[planName].yearly;

      const primaryPlan = monthlyPlan || yearlyPlan;

      return {
        monthlyId: monthlyPlan?._id,
        yearlyId: yearlyPlan?._id,
        name: planName,
        description: config?.description || "Subscription plan",
        monthlyPrice: monthlyPlan?.price || 0,
        yearlyPrice: yearlyPlan?.discountedPrice || 0,
        yearlyTotal: yearlyPlan?.price || 0,
        creditsMonthly: primaryPlan?.creditsMonthly || 0,
        topUpCredits: primaryPlan?.topUpCredits || 0,
        rechargePrice: primaryPlan?.rechargePrice || 10,
        firstColor: config.firstColor,
        features: getPlanFeatures(primaryPlan),
        color: config?.color || "#ccc",
        gradient: config?.gradient || "linear-gradient(135deg, #ccc, #999)",
        popular: config?.popular || false,
      };
    });

    if (user?.subscription?.name) {
      const dynamicRechargeConfig = {
        ...rechargeConfig,
        creditsAmount: getActiveUserCredits(),
      };
      plans.push(dynamicRechargeConfig);
    }

    return plans;
  };

  const handleSubscriptionExpiredModalClose = () => {
    setIsSubscriptionExpiredModalOpen(false);
  };

  const handleSubscriptionExpiredModalOpen = () => {
    setIsSubscriptionExpiredModalOpen(true);
  };

  const handleSubscriptionExpiredModalConfirm = () => {
    renewPlan();
  };

  const handlePageChange = (newPage) => {
    dispatch(getSubscriptionsCreditsHistory(newPage))
      .unwrap()
      .catch((err) => console.log(err.message));
  };

  const plans = prepareSubscriptionPlans();

  const LoadingSpinner = () => (
    <PlansGrid>
      <LoadingIndicator>
        <l-line-spinner size="24" stroke="2" speed="1.5" color="#2877D9" />
        <span>Loading subscriptions ...</span>
      </LoadingIndicator>
    </PlansGrid>
  );

  return (
    <MainContainer>
      <BackButton onClick={handleBack}>
        <ChevronSvg />
        Back
      </BackButton>
      <Container>
        <Title>Subscription</Title>

        <PricingSection>
          {user && (
            <>
              <CurrentPlanSection>
                <PlanInfo>
                  {isExpiredSubscription ? (
                    <PlanValue>
                      Free Plan {userHadSubscription && "(Expired)"}
                    </PlanValue>
                  ) : (
                    <PlanValue>
                      {user?.subscription?.name || "Free"} Plan{" "}
                      {yearlySubscription && "Yearly"}
                    </PlanValue>
                  )}
                  <InfoSvg onClick={() => setIsManageModalOpen(true)}></InfoSvg>
                </PlanInfo>
                <CreditsInfo>
                  <CreditsContainer>
                    <CreditsLabel>Credits:</CreditsLabel>
                    <GradientStarIcon />
                    <CreditsValue>{priceFormatter(user?.credits)}</CreditsValue>
                  </CreditsContainer>

                  {userHadSubscription && (
                    <RenewButton
                      onClick={() => {
                        if (isActiveSubscription) {
                          handleGetStarted(
                            plans.find((plan) => plan.name === "Recharge")
                          );
                        } else {
                          renewPlan();
                        }
                      }}
                    >
                      {isActiveSubscription ? "Recharge" : "Renew"}
                    </RenewButton>
                  )}
                </CreditsInfo>
                {/* <Button onClick={() => setIsManageModalOpen(true)}>
                  {isMobile ? "Manage" : "Manage Subscription"}
                </Button> */}
              </CurrentPlanSection>

              <SeparatorLine></SeparatorLine>

              <BalanceSection>
                <SectionHeader onClick={toggleBalance}>
                  <SectionTitle>Current Balance Details</SectionTitle>
                  <FaChevronUp
                    size={14}
                    style={{
                      transition: "transform 0.3s ease-in-out",
                      transform: isBalanceExpanded
                        ? "rotate(0deg)"
                        : "rotate(180deg)",
                      cursor: "pointer",
                      opacity: 0.7,
                    }}
                  />
                </SectionHeader>
                <TableContainer $isExpanded={isBalanceExpanded}>
                  <Table>
                    <thead>
                      <TableRow>
                        <TableHeader>Amount</TableHeader>
                        <TableHeader>Balance</TableHeader>
                        <TableHeader>Start Time</TableHeader>
                        <TableHeader>Expiration</TableHeader>
                        <TableHeader>Plan</TableHeader>
                      </TableRow>
                    </thead>
                    <tbody>
                      {creditsHistoryLoading ? (
                        <TableCell colSpan={3} style={{ textAlign: "center" }}>
                          <TableLoading>
                            <l-line-spinner
                              size="24"
                              stroke="2"
                              speed="1.5"
                              color="#fff"
                            />
                            <span>Loading balance details...</span>
                          </TableLoading>
                        </TableCell>
                      ) : (
                        creditsHistoryData.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell
                              $isFirst={true}
                              style={{ color: "#fff" }}
                            >
                              {priceFormatter(item?.numOfCredits)}
                            </TableCell>
                            <TableCell style={{ color: "#fff" }}>
                              {priceFormatter(item?.activeBalance)}
                            </TableCell>
                            <TimeAgoCell
                              onMouseEnter={() => setHoveredStartTime(index)}
                              onMouseLeave={() => setHoveredStartTime(null)}
                            >
                              {item.createdAt && formatTimeAgo(item.createdAt)}
                              <Tooltip $show={hoveredStartTime === index}>
                                {item.createdAt && formatDate(item.createdAt)}
                              </Tooltip>
                            </TimeAgoCell>
                            <TimeAgoCell
                              onMouseEnter={() => setHoveredExpiration(index)}
                              onMouseLeave={() => setHoveredExpiration(null)}
                            >
                              {item?.expirationDate &&
                                !NOT_EXPIRATION_SALE_LOGS_TYPES.includes(
                                  item?.type
                                ) &&
                                daysDifference(item?.expirationDate)}
                              {item?.expirationDate &&
                                !NOT_EXPIRATION_SALE_LOGS_TYPES.includes(
                                  item?.type
                                ) && (
                                  <Tooltip $show={hoveredExpiration === index}>
                                    {item?.expirationDate &&
                                      formatDate(item?.expirationDate)}
                                  </Tooltip>
                                )}
                            </TimeAgoCell>
                            <TableCell>
                              {item?.planName ? item?.planName : item?.type}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </tbody>
                  </Table>

                  <TableFooter>
                    <FooterActions>
                      <FooterText>
                        Showing {creditsHistoryData.length} of{" "}
                        {creditsHistoryTotal} grants
                      </FooterText>
                    </FooterActions>
                    <FooterActions>
                      <Pagination>
                        <PaginationButton
                          disabled={creditsHistoryPage <= 1}
                          onClick={() =>
                            handlePageChange(creditsHistoryPage - 1)
                          }
                        >
                          Previous
                        </PaginationButton>

                        <PaginationCurrent>
                          Page {creditsHistoryPage} of{" "}
                          {Math.ceil(
                            creditsHistoryTotal / creditsHistoryPerPage
                          )}
                        </PaginationCurrent>

                        <PaginationButton
                          disabled={
                            creditsHistoryPage >=
                            Math.ceil(
                              creditsHistoryTotal / creditsHistoryPerPage
                            )
                          }
                          onClick={() =>
                            handlePageChange(creditsHistoryPage + 1)
                          }
                        >
                          Next
                        </PaginationButton>
                      </Pagination>
                    </FooterActions>
                  </TableFooter>
                </TableContainer>
              </BalanceSection>
            </>
          )}
        </PricingSection>

        <BillingToggle>
          <img
            src={giftgif}
            style={{ marginBottom: "5px" }}
            width={25}
            height={25}
            alt="..."
          />
          <BillingText>Save Up To 50% with Annual Billing</BillingText>
          <ToggleContainer
            $rightValueSelected={billingPeriod === "yearly"}
            onClick={() => {
              setBillingPeriod(
                billingPeriod === "yearly" ? "monthly" : "yearly"
              );
            }}
          >
            <ToggleButton $rightValueSelected={billingPeriod === "yearly"} />
          </ToggleContainer>
        </BillingToggle>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <PlansGrid>
            {plans.map((plan, index) =>
              plan.name === "Recharge" ? (
                <PlanCard key={index} $gradient={plan.gradient}>
                  <HeaderContainer>
                    <PlanDescription>{plan.description}</PlanDescription>
                    <PlanName>{plan.name}</PlanName>
                  </HeaderContainer>

                  <PriceSection>
                    <CurrentPrice>${plan.price}</CurrentPrice>
                    <PricePeriod>
                      /
                      {`${priceFormatter(plan.creditsAmount)} credits` ||
                        "300 credits"}
                    </PricePeriod>
                  </PriceSection>

                  <YearlyTotal>One time payment</YearlyTotal>

                  <FeaturesList>
                    {plan.features.map((feature, featureIndex) => (
                      <Feature key={featureIndex}>
                        <CheckSvg color="#ffffff" />
                        {feature}
                      </Feature>
                    ))}
                  </FeaturesList>

                  <GetStartedButton onClick={() => handleGetStarted(plan)}>
                    Get more Credits
                  </GetStartedButton>
                </PlanCard>
              ) : (
                <PlanCard
                  key={plan.id || index}
                  $gradient={plan.gradient}
                  $active={plan.name === "Pro"}
                  $color={plan.color}
                >
                  {plan.popular && <PopularBadge>Most Popular</PopularBadge>}
                  <HeaderContainer>
                    <PlanDescription>{plan.description}</PlanDescription>
                    <PlanName>{plan.name}</PlanName>
                  </HeaderContainer>

                  <PriceSection>
                    {billingPeriod === "yearly" && (
                      <OriginalPrice>${plan.monthlyPrice}</OriginalPrice>
                    )}
                    <CurrentPrice>
                      $
                      {billingPeriod === "yearly"
                        ? plan.yearlyPrice.toFixed(2)
                        : plan.monthlyPrice}
                    </CurrentPrice>
                    <PricePeriod>/month</PricePeriod>
                  </PriceSection>
                  <GetStartedButton
                    $isActive={
                      user?.subscription &&
                      plan?.name === user?.subscription?.name
                    }
                    onClick={() => handleGetStarted(plan)}
                  >
                    Get Started
                  </GetStartedButton>

                  <FeaturesList>
                    {plan.features.map((feature, featureIndex) => (
                      <Feature
                        key={featureIndex}
                        $first={featureIndex === 0}
                        $firstColor={plan.firstColor}
                      >
                        <CheckSvg
                          color={featureIndex === 0 ? plan.firstColor : "#fff"}
                        />
                        {feature}
                      </Feature>
                    ))}
                  </FeaturesList>
                </PlanCard>
              )
            )}
          </PlansGrid>
        )}

        <FAQSection>
          <SectionTitle>Frequently Asked Questions</SectionTitle>
          <FAQList>
            {faqData.map((faq, index) => (
              <FAQItem key={index}>
                <FAQQuestion onClick={() => toggleFAQ(index)}>
                  <span>{faq.question}</span>
                  <FaChevronUp
                    style={{
                      transition: "transform 0.3s ease-in-out",
                      transform:
                        expandedFAQ === index
                          ? "rotate(0deg)"
                          : "rotate(180deg)",
                    }}
                  />
                </FAQQuestion>
                {(expandedFAQ === index || exitingFAQ === index) && (
                  <FAQAnswer $isExiting={exitingFAQ === index}>
                    {faq.answer}
                  </FAQAnswer>
                )}
              </FAQItem>
            ))}
          </FAQList>
        </FAQSection>
      </Container>
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={handleClosePaymentModal}
        selectedPlan={selectedPlan}
        billingPeriod={billingPeriod}
        handleCardPayment={handleCardPayment}
      />
      <RechargeModal
        isOpen={isRechargeModalOpen}
        onClose={handleCloseRechargeModal}
        selectedPlan={selectedPlan}
        billingPeriod={billingPeriod}
        handleCardPayment={handleCardPayment}
      />
      <ManageSubscriptionModal
        isOpen={isManageModalOpen}
        onClose={handleCloseManageModal}
      />
      <SubscriptionExpiredModal
        isOpen={isSubscriptionExpiredModalOpen}
        onClose={handleSubscriptionExpiredModalClose}
        onConfirm={handleSubscriptionExpiredModalConfirm}
      />
    </MainContainer>
  );
};

export default SubscriptionPage;
